// Campo de texto padrão. Unifica o `AuthInput` que existia duplicado em Login.js,
// Registrar.js e TrocarSenha.js com o `Input` de AdminUsuarios.js e
// GerenciarMatriculas.js — eram praticamente o mesmo componente com nomes diferentes.
//
// `onChange` recebe o evento nativo do input (`e => ...`, não o valor direto) —
// combina com como a maioria dos formulários do sistema já lida com estado.
//
// Encaminha `ref` (React.forwardRef) para funcionar também com
// `react-hook-form` (`<Input {...register('email')} />`, como em Login.js) —
// nesse uso o campo fica não controlado (sem `value`), o próprio register cuida disso.
import React, { forwardRef } from 'react';

const Input = forwardRef(function Input(
  {
    label,
    type = 'text',
    name,
    value,
    onChange,
    onBlur,
    onKeyDown,
    required,
    minLength,
    maxLength,
    pattern,
    inputMode,
    disabled,
    placeholder,
    autoComplete,
    icon: Icon,
    size = 'md',
    error,
    errorMessage,
    className = '',
    step,
  },
  ref,
) {
  const height = size === 'lg' ? 'h-11' : size === 'sm' ? 'h-8' : size === 'xs' ? 'h-6' : 'h-10';
  const textSize = size === 'xs' ? 'text-[11px]' : size === 'sm' ? 'text-xs' : 'text-sm';
  const paddingClass = Icon
    ? size === 'xs'
      ? 'pl-6 pr-2'
      : size === 'sm'
        ? 'pl-7 pr-3'
        : 'pl-9 pr-4'
    : size === 'xs'
      ? 'px-2'
      : size === 'sm'
        ? 'px-2.5'
        : 'px-3';
  const borderClass = error
    ? 'border-red-300 dark:border-red-800 focus:ring-red-500/20 focus:border-red-500'
    : 'border-gray-200 dark:border-gray-700 focus:ring-blue-500/20 focus:border-blue-500';
  const labelSize = size === 'xs' ? 'text-[10px]' : 'text-xs';
  const spacing = size === 'xs' ? 'space-y-0.5' : 'space-y-1.5';
  const iconSize =
    size === 'xs' ? 'w-3 h-3 left-2' : size === 'sm' ? 'w-3.5 h-3.5 left-2.5' : 'w-4 h-4 left-2.5';

  return (
    <div className={`${spacing} ${className}`}>
      {label && (
        <label className={`block ${labelSize} font-medium text-gray-500 dark:text-gray-400`}>
          {label}
        </label>
      )}
      <div className="relative">
        {Icon && <Icon className={`text-gray-400 absolute top-1/2 -translate-y-1/2 ${iconSize}`} />}
        <input
          ref={ref}
          type={type}
          step={step}
          name={name}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          required={required}
          minLength={minLength}
          maxLength={maxLength}
          pattern={pattern}
          inputMode={inputMode}
          disabled={disabled}
          placeholder={placeholder}
          autoComplete={autoComplete}
          className={`w-full ${height} ${paddingClass} rounded-xl border bg-white dark:bg-gray-800 ${textSize} text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 transition-all disabled:bg-gray-50 dark:disabled:bg-gray-900 disabled:text-gray-400 ${borderClass}`}
        />
      </div>
      {errorMessage && (
        <p className="text-[10px] text-red-500 dark:text-red-400 mt-1">{errorMessage}</p>
      )}
    </div>
  );
});

export default Input;
